import React, { useRef } from "react";
import PropTypes from "kf-proptypes/widgets";

import { i18n } from "base/i18n";

import { Modal } from "@widgets/datadisplay/modal/index.jsx";
import { BUTTON_SIZES, BUTTON_TYPES, Button } from "@widgets/inputs/index.jsx";
import {
  KfIcon,
  ICON_STROKE,
  KF_ICONS,
  ICON_SIZE
} from "@widgets/common/components/primitives/kf_icon/index.jsx";

import styles from "./styles.css";

Popup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
  style: PropTypes.shape({
    display: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  })
};

/**
 * Popup Component
 * @param {boolean} isOpen - state to set modal open.
 * @param {node} children
 */

export function Popup({ isOpen, children, style }) {
  const modalRef = useRef();

  return (
    <div ref={modalRef}>
      <Modal
        titleRenderer={null}
        closable={false}
        clickOutToClose={false}
        open={isOpen}
        closeOnEscape={false}
        footerRenderer={() => null}
        rightActionsRenderer={() => {}}
        width={"100%"}
        // property_wrapper is the id of the element inside Property component. Do not change this.
        getContainer={() => modalRef.current?.closest("#property_wrapper")}
        centered={false}
        backgroundClassName={styles.backgroundMask}
        bodyStyle={style}
      >
        {children}
      </Modal>
    </div>
  );
}

Header.propTypes = {
  label: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired
};
/**
 * Header - Header of Popup component
 * @param {string} label - Mark the label with defineMessage and pass it here.
 * @param {function} onClose - function to close the Popup
 */
export function Header({ label, onClose }) {
  return (
    <div className={styles.header}>
      <span className={"smallplus-600"}>{i18n._(label)}</span>
      <KfIcon
        icon={KF_ICONS.CLOSE}
        size={ICON_SIZE.SIZE_12}
        stroke={ICON_STROKE.THIN}
        className={styles.closeIcon}
        onClick={(event) => {
          onClose();
          event.stopPropagation();
        }}
      />
    </div>
  );
}

Body.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string
};
/**
 * Body - Body of Popup component - Consumer can define this component respectively with primitives.
 * @param {node} children
 * @param {String} className
 */
export function Body({ children, className }) {
  return <div className={`${styles.body} ${className}`}>{children}</div>;
}

Footer.propTypes = {
  okLabel: PropTypes.string.isRequired,
  onOk: PropTypes.func.isRequired,
  okDisabled: PropTypes.bool,
  cancelLabel: PropTypes.string,
  onCancel: PropTypes.func
};
/**
 * Footer - Footer of Popup component
 * @param {string} okLabel - Primary action label. Mark the label with defineMessage and pass it here.
 * @param {function} onOk - function which defines the action to be performed on clicking Primary button.
 * @param {bool} okDisabled - Disable/enable Primary button.
 * @param {string} cancelLabel - Secondary action label. Mark the label with defineMessage and pass it here.
 * @param {function} onCancel - function which defines the action to be performed on clicking Secondary button.
 */
export function Footer({ okLabel, onOk, okDisabled, cancelLabel, onCancel }) {
  return (
    <div className={styles.footer}>
      {cancelLabel && (
        <Button
          type={BUTTON_TYPES.SECONDARY}
          size={BUTTON_SIZES.MEDIUM}
          onClick={onCancel}
        >
          {i18n._(cancelLabel)}
        </Button>
      )}
      <Button
        type={BUTTON_TYPES.PRIMARY}
        size={BUTTON_SIZES.MEDIUM}
        onClick={onOk}
        disabled={okDisabled}
      >
        {i18n._(okLabel)}
      </Button>
    </div>
  );
}

Popup.Header = Header;
Popup.Body = Body;
Popup.Footer = Footer;
